import React, { useState, useEffect, useCallback } from 'react';
import { studentsAPI } from '../services/api';

// Document submission tracker (Q7/Q9): staff-set applicant category + flags
// that drive which visa-checklist items get auto-seeded for this student.
const panelStyle = {
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  marginTop: 12,
};
const headingStyle = { color: '#9aa4c4', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 };
const labelStyle = { fontSize: 11, color: '#9aa4c4', display: 'block', marginTop: 8, marginBottom: 4 };
const selectStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)', color: '#eee', fontSize: 12, boxSizing: 'border-box',
};
const checkboxRowStyle = { display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, color: '#ddd', fontSize: 12 };

const CATEGORIES = [
  ['', 'Not set'],
  ['SELF_FUNDED', 'Self-funded adult'],
  ['EMPLOYEE', 'Employee'],
  ['COMPANY_OWNER', 'Company owner'],
  ['SPONSORED_BY_PERSON', 'Sponsored by a person'],
  ['MINOR_UNDER_18', 'Minor (under 18)'],
];

const VisaProfilePanel = ({ studentId, onProfileChanged }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await studentsAPI.getVisaProfile(studentId);
      setProfile(res.data);
    } catch (err) {
      setError('Could not load the visa profile.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const res = await studentsAPI.updateVisaProfile(studentId, patch);
      setProfile(res.data);
      if (onProfileChanged) await onProfileChanged();
    } catch (err) {
      setError('Could not save the visa profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return (
      <div style={panelStyle}>
        <div style={headingStyle}>VISA APPLICANT PROFILE</div>
        <p style={{ color: '#9aa4c4', fontSize: 12 }}>Loading…</p>
      </div>
    );
  }

  const Checkbox = ({ field, label }) => (
    <label style={checkboxRowStyle}>
      <input type="checkbox" checked={!!profile[field]} disabled={saving}
        onChange={(e) => save({ [field]: e.target.checked })} />
      {label}
    </label>
  );

  return (
    <div style={panelStyle}>
      <div style={headingStyle}>VISA APPLICANT PROFILE</div>
      {error && <div style={{ color: '#f5576c', fontSize: 12, marginBottom: 8 }}>{error}</div>}

      <label style={labelStyle}>Applicant category</label>
      <select style={selectStyle} value={profile.applicant_category} disabled={saving}
        onChange={(e) => save({ applicant_category: e.target.value })}>
        {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>

      {profile.applicant_category === 'EMPLOYEE' && (
        <>
          <Checkbox field="has_zimbabwe_bank_account" label="Has a Zimbabwe bank account" />
          {!profile.has_zimbabwe_bank_account && (
            <>
              <label style={labelStyle}>Paid in</label>
              <select style={selectStyle} value={profile.payment_method} disabled={saving}
                onChange={(e) => save({ payment_method: e.target.value })}>
                <option value="">Not set</option>
                <option value="CASH">Cash</option>
                <option value="EXCHANGE">Exchange</option>
              </select>
            </>
          )}
        </>
      )}

      {profile.applicant_category === 'SPONSORED_BY_PERSON' && (
        <>
          <Checkbox field="sponsor_is_spouse" label="Sponsor is the applicant's spouse" />
          <Checkbox field="sponsor_is_employed" label="Sponsor is employed" />
          <Checkbox field="sponsor_is_company_owner" label="Sponsor is a company owner" />
          <Checkbox field="sponsor_outside_zimbabwe" label="Sponsor is outside Zimbabwe" />
        </>
      )}

      {profile.applicant_category === 'MINOR_UNDER_18' && (
        <>
          <Checkbox field="parents_divorced" label="Parents are divorced" />
          <Checkbox field="parent_deceased" label="A parent is deceased" />
          <Checkbox field="parent_not_travelling" label="One parent is not travelling" />
          <Checkbox field="parent_outside_country" label="A parent is outside the country" />
        </>
      )}

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '10px 0' }} />

      <Checkbox field="is_dual_citizen" label="Dual citizen" />
      <Checkbox field="is_foreign_resident_in_zimbabwe" label="Foreign national resident in Zimbabwe" />
      <Checkbox field="still_student_elsewhere" label="Still enrolled as a student elsewhere" />
      <Checkbox field="is_family_application" label="Applying as part of a family group" />

      <label style={labelStyle}>Residence permit — proof of address via</label>
      <select style={selectStyle} value={profile.address_proof_method} disabled={saving}
        onChange={(e) => save({ address_proof_method: e.target.value })}>
        <option value="">Not set</option>
        <option value="E_DEVLET">e-Devlet residence document</option>
        <option value="RENTAL_AGREEMENT">Notarized rental agreement</option>
      </select>

      <p style={{ color: '#8a94b8', fontSize: 11, margin: '10px 0 0' }}>
        Changing these only adds/removes the matching checklist items above — anything already uploaded or received stays untouched.
      </p>
    </div>
  );
};

export default VisaProfilePanel;
